<template>
  <div class="system-roles">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>角色管理</span>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleCreate">添加角色</el-button>
      </div>

      <el-table :data="list" border stripe v-loading="loading" style="width: 100%">
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="name" label="角色名称" width="160" />
        <el-table-column prop="slug" label="角色标识" width="160" />
        <el-table-column prop="description" label="描述" min-width="200" />
        <el-table-column label="状态" width="80">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'" size="small">
              {{ scope.row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="160" />
        <el-table-column label="操作" width="180" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" size="small" style="color: #E4393C" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap" v-if="total > 0">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :total="total"
          :page-size="query.limit"
          :current-page.sync="query.page"
          @current-change="getList"
        />
      </div>
    </el-card>

    <!-- 添加/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="580px" :close-on-click-modal="false">
      <el-form ref="form" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="角色名称" prop="name">
          <el-input v-model="form.name" placeholder="如：超级管理员" maxlength="50" />
        </el-form-item>
        <el-form-item label="角色标识" prop="slug">
          <el-input v-model="form.slug" placeholder="如：admin" maxlength="50" />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="form.description" placeholder="角色描述" maxlength="200" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="分配权限">
          <div class="perm-tip">未勾选的菜单该角色登录后不再展示，也不能通过地址直接访问；未勾选「首页数据」时登录落地页会自动跳到第一个有权限的菜单。</div>
          <el-tree
            ref="permTree"
            :data="permTree"
            show-checkbox
            node-key="id"
            :default-checked-keys="form.permission_ids"
            :props="{ label: 'label', children: 'children' }"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getRbacRoles, createRbacRole, updateRbacRole, deleteRbacRole, getRbacPermissionTree } from '@/api'

export default {
  name: 'SystemRoles',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      query: { page: 1, limit: 15 },
      dialogVisible: false,
      dialogTitle: '',
      isEdit: false,
      editId: null,
      submitLoading: false,
      permTree: [],
      form: {
        name: '',
        slug: '',
        description: '',
        status: 1,
        permission_ids: []
      },
      rules: {
        name: [{ required: true, message: '请输入角色名称', trigger: 'blur' }],
        slug: [{ required: true, message: '请输入角色标识', trigger: 'blur' }]
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    getList() {
      this.loading = true
      getRbacRoles(this.query).then(res => {
        this.list = res.list || []
        this.total = res.total || 0
      }).finally(() => { this.loading = false })
    },
    fetchPermTree() {
      getRbacPermissionTree().then(res => {
        this.permTree = res || []
      }).catch(() => {})
    },
    resetForm() {
      this.form = { name: '', slug: '', description: '', status: 1, permission_ids: [] }
    },
    handleCreate() {
      this.isEdit = false
      this.editId = null
      this.dialogTitle = '添加角色'
      this.resetForm()
      this.fetchPermTree()
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
        this.$refs.permTree && this.$refs.permTree.setCheckedKeys([])
      })
    },
    handleEdit(row) {
      this.isEdit = true
      this.editId = row.id
      this.dialogTitle = '编辑角色'
      this.form = {
        name: row.name,
        slug: row.slug,
        description: row.description || '',
        status: row.status,
        permission_ids: row.permissions || []
      }
      this.fetchPermTree()
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    handleSubmit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        this.submitLoading = true
        // 获取选中的权限
        const checkedKeys = this.$refs.permTree ? this.$refs.permTree.getCheckedKeys() : []
        const halfCheckedKeys = this.$refs.permTree ? this.$refs.permTree.getHalfCheckedKeys() : []
        const data = {
          ...this.form,
          permission_ids: [...checkedKeys, ...halfCheckedKeys]
        }

        const apiCall = this.isEdit
          ? updateRbacRole(this.editId, data)
          : createRbacRole(data)

        apiCall.then(() => {
          this.$message.success(this.isEdit ? '更新成功' : '创建成功')
          this.dialogVisible = false
          this.getList()
        }).finally(() => { this.submitLoading = false })
      })
    },
    handleDelete(row) {
      this.$confirm(`确认删除角色 "${row.name}"？删除后关联的管理员将失去此角色的权限。`, '提示', { type: 'warning' }).then(() => {
        deleteRbacRole(row.id).then(() => {
          this.$message.success('删除成功')
          this.getList()
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.perm-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
  margin-bottom: 6px;
}
.pagination-wrap {
  margin-top: 16px;
  text-align: right;
}
</style>
